import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Beaker, ShieldCheck, FlaskConical, Atom } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_BG = "https://images.unsplash.com/photo-1643780668909-580822430155?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} className="w-full h-full object-cover opacity-30" alt="" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/85 to-slate-950" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 relative">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-cyan-400 rounded-full pulse-cyan" />
            <span className="label-mono text-cyan-400">NGX//RESEARCH-GRADE PEPTIDES</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.95] max-w-4xl text-balance">
            Precision peptides,<br />
            <span className="text-cyan-400 cyan-text-glow">engineered for discovery.</span>
          </h1>

          <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
            High-purity research compounds synthesized in our laboratories. Every batch independently verified by HPLC and mass spectrometry — so your data isn&apos;t.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/shop" data-testid="hero-shop-cta" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-7 py-3.5 tracking-wide transition-colors">
              Browse Catalog <ArrowRight size={16} />
            </Link>
            <Link to="/qr" data-testid="hero-qr-cta" className="inline-flex items-center gap-2 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 px-7 py-3.5 tracking-wide transition-colors">
              Get QR Access
            </Link>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { k: ">99%", l: "HPLC Purity" },
              { k: "48 hrs", l: "Avg Dispatch" },
              { k: "8+", l: "Active Compounds" },
              { k: "0", l: "Failed Batches '25" },
            ].map((s) => (
              <div key={s.l} className="bg-slate-950 p-6">
                <div className="font-mono text-3xl text-cyan-400">{s.k}</div>
                <div className="label-mono text-slate-500 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <div className="label-mono text-cyan-400 mb-4">/ 01 — METHODOLOGY</div>
            <h2 className="text-4xl font-medium tracking-tight leading-tight">
              Lab-grade by default.<br />Not by exception.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {[
              { i: FlaskConical, t: "Solid-phase synthesis", d: "Fmoc-based SPPS for sequence fidelity and yield consistency across every batch." },
              { i: ShieldCheck, t: "Third-party verified", d: "Independent HPLC + mass spectrometry for every lot. COA available on request." },
              { i: Atom, t: "Pharmaceutical excipients", d: "Lyophilized with research-appropriate stabilizers. Designed for >24-month shelf life at −20°C." },
              { i: Beaker, t: "Discreet cold-chain", d: "Insulated dispatch within 48 hours. Tracked, signed-for delivery worldwide." },
            ].map((p) => (
              <div key={p.t} className="bg-slate-950 p-8">
                <p.i className="text-cyan-400 mb-4" size={24} strokeWidth={1.5} />
                <div className="font-medium text-lg">{p.t}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono text-cyan-400 mb-3">/ 02 — FEATURED COMPOUNDS</div>
            <h2 className="text-4xl font-medium tracking-tight">Most-ordered this quarter</h2>
          </div>
          <Link to="/shop" data-testid="view-all-link" className="hidden sm:inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300">
            View entire catalog <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="relative border border-cyan-500/20 p-12 md:p-20 overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30 -z-10" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="label-mono text-cyan-400 mb-4">/ INSTITUTIONAL ACCOUNTS</div>
              <h3 className="text-3xl md:text-4xl font-medium tracking-tight">
                Procuring for a research institution?
              </h3>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Bulk pricing, NET-30 terms, and dedicated compound development available for verified research entities.
              </p>
              <a href="mailto:research@novagenix.com" data-testid="contact-research-link" className="mt-6 inline-flex items-center gap-2 bg-cyan-500 text-slate-950 px-6 py-3 font-semibold">
                Contact Research Team <ArrowRight size={16} />
              </a>
            </div>
            <div className="hidden md:block">
              <img src="/logo-transparent.png" alt="" className="w-full max-w-xs ml-auto float-slow opacity-90" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
